// `slide-apiophobia` + its three reveals (`APIOPHOBIA_MAX_REVEAL = 3`): the word alone, then its
// two word-origins underneath, marked English at the end (docs/PRESENTATION.md, "On language").
//
// The reveal is laid out as an equation — two source fragments, a plus, an equals, the English
// result — because that is exactly the sentence the presenter says over it. Set as three loose
// text blocks it read as a ragged row whose "= Apiophobia (English)" hung off the end at a
// different baseline from the fragments beside it (measured at 1920×1080).
//
// **Two things the 2026-08-11 polish pass changed, and both are about the word not moving.**
//
//  - The row's box is **reserved from step 0**: every slot is a fixed-width, fixed-height box that
//    is present and empty before its content arrives. The row used to be `null` until the one
//    reveal landed, so the hero word was vertically centred on its own and then jumped upward when
//    the equation appeared under it (user story 3/7). Reserving is done with *empty* slots rather
//    than hidden content on purpose: an invisible-but-rendered fragment is still in the accessibility
//    tree and still in the slide's text, which is not what "not revealed yet" means.
//  - The reveal order is **`-phobia`, then `api-` with its plus, then `=` and the English result** —
//    the order the presenter says the sentence in, which is not the order the equation reads
//    (user story 4). The *layout* order is unchanged; only what is filled in when.

import type { ReactElement, ReactNode } from 'react';
import { SlideFrame } from './SlideFrame';
import { SLIDE_SCALE } from './scale';
import { APIOPHOBIA_ORIGINS, APIOPHOBIA_RESULT, APIOPHOBIA_WORD } from './content';
import './slides.css';

/** Every slot in the row, sized at 1920×1080 so the widest gloss each can hold fits on one line.
 * These are what make the reservation exact: a `Term` fills its slot rather than sizing itself. */
const FRAGMENT_SLOT = 340;
const RESULT_SLOT = 440;
const OPERATOR_SLOT = 72;
const SLOT_HEIGHT = 152;

function Slot({ width, children }: { width: number; children?: ReactNode }): ReactElement {
  return (
    <div
      style={{
        width,
        height: SLOT_HEIGHT,
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </div>
  );
}

function Term({
  term,
  gloss,
  hot = false,
  testId,
}: {
  term: string;
  gloss: string;
  hot?: boolean;
  testId?: string;
}): ReactElement {
  return (
    <div
      className="demo-reveal"
      data-testid={testId}
      style={{
        width: '100%',
        background: 'var(--deck-card)',
        border: '3px solid var(--deck-secondary)',
        borderRadius: 'var(--r-md)',
        padding: '20px 24px',
        textAlign: 'center',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-body)',
          fontWeight: 600,
          fontSize: SLIDE_SCALE.body,
          color: hot ? 'var(--deck-secondary)' : 'var(--deck-ink)',
        }}
      >
        {term}
      </div>
      <div
        style={{
          fontSize: SLIDE_SCALE.caption,
          fontWeight: 600,
          color: 'var(--deck-ink)',
          marginTop: 6,
        }}
      >
        {gloss}
      </div>
    </div>
  );
}

function Operator({ symbol }: { symbol: string }): ReactElement {
  return (
    <span
      aria-hidden
      className="demo-reveal"
      style={{
        fontFamily: 'var(--font-display)',
        fontWeight: 600,
        fontSize: SLIDE_SCALE.title,
        color: 'var(--deck-secondary)',
        lineHeight: 1,
      }}
    >
      {symbol}
    </span>
  );
}

export function ApiophobiaSlide({ revealStep }: { revealStep: number }): ReactElement {
  const [latin, greek] = APIOPHOBIA_ORIGINS;
  return (
    <SlideFrame center>
      <h1
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 600,
          fontSize: SLIDE_SCALE.hero,
          color: 'var(--deck-ink)',
          letterSpacing: '-0.02em',
          margin: 0,
        }}
      >
        {APIOPHOBIA_WORD}
      </h1>
      <div
        data-testid="apiophobia-equation"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--sp-6)',
          marginTop: 24,
        }}
      >
        {/* Step 2 fills the Latin fragment and the plus that joins it to the Greek one…
            Each slot's testid is its own **cue's** beat id (`cues.ts`), so the rehearsal walk can
            assert one reveal at a time rather than "something changed on this slide". */}
        <Slot width={FRAGMENT_SLOT}>
          {revealStep >= 2 ? (
            <Term
              term={latin!.fragment}
              gloss={`${latin!.origin} for “${latin!.meaning}”`}
              testId="reveal-apiophobia-api"
            />
          ) : undefined}
        </Slot>
        <Slot width={OPERATOR_SLOT}>{revealStep >= 2 ? <Operator symbol="+" /> : undefined}</Slot>
        {/* …but step 1 fills THIS one. The presenter says "-phobia" first. */}
        <Slot width={FRAGMENT_SLOT}>
          {revealStep >= 1 ? (
            <Term
              term={greek!.fragment}
              gloss={`${greek!.origin} for “${greek!.meaning}”`}
              testId="reveal-apiophobia-phobia"
            />
          ) : undefined}
        </Slot>
        <Slot width={OPERATOR_SLOT}>{revealStep >= 3 ? <Operator symbol="=" /> : undefined}</Slot>
        <Slot width={RESULT_SLOT}>
          {revealStep >= 3 ? (
            <Term
              term={APIOPHOBIA_WORD}
              gloss={APIOPHOBIA_RESULT}
              hot
              testId="reveal-apiophobia-origins"
            />
          ) : undefined}
        </Slot>
      </div>
    </SlideFrame>
  );
}
